import React, { useState } from "react";
import Modal from "../Modal";
import styled from '@emotion/styled';
import { Alert } from "../Alert";
import { StyledModal } from "../../StyledComponents/Modals/Modals";
import { CenterContainer, FormContainer } from "../../StyledComponents/Forms/Forms";
import Card from "../Card";
import { CancelButton } from "../../StyledComponents/Buttons/Buttons";
import { FieldContainer } from "../../StyledComponents/Forms/Forms";
import TextInputWithLabel from "../../Inputs/TextInput";
import RichTextBoxWithLabel from "../../Inputs/RichTextBoxWithLabel";
import { authoriseOrder } from "../../../http/distributor";
import { ModalBody } from "../../StyledComponents/Modals/Modals";
import { LoadingScreen } from "../../StyledComponents/Forms/Loader";
import SelectWithLabel, { YesNoSelect } from "../../Inputs/Select";

const ModalFooter = styled.div`
  text-align: right;
  flex-shrink: 0;
  padding: 10px 0px;
`;

const Heading = styled.h3`
  margin: 0;
  margin-top: 10px;
  padding-bottom: 5px;
`;

const Paragraph = styled.p`
  margin: 0 0 20px 0;
  font-size:15px;
`;

const Button = styled.button`
  background-color: #93C572;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  padding: 10px 20px;
  margin-right: 10px;

  &:hover {
    background-color: #698D51;
  }

  &:focus {
    outline: none;
  }

  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`;

const ScheduleWrapper = styled.div`
  max-height: 400px;
  overflow-y: auto;
  width:100%;
`;

export default function AuthoriseEyeTicket({
    showHideModal,
    isModalOpen,
    colour,
    eyeTicket
}) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [ticketDetails, setTicketDetails] = useState(eyeTicket);
    const [missingFields, setMissingFields] = useState({});

    const [acceptItems, setAcceptItems] = useState('');
    const [acceptLens, setAcceptLens] = useState('');

    const selectOptions = [
        { label: 'Select A Value', value: '' },
        { label: 'Reject', value: 'Reject' },
        { label: 'Accept', value: 'Accept' },
    ];

    function handleEnteredValues(identifier, value) {
        setTicketDetails((prevValues) => ({
            ...prevValues,
            [identifier]: value
        }));
    }

    const validateForm = () => {
        let newMissingFields = {};
        let isValid = true;

        if (ticketDetails.OrderNumberRequired === 1 && ticketDetails.OrderNumber === '') {
            newMissingFields.OrderNumber = true;
        }

        if (ticketDetails.AcceptUnauthorisedItems && (acceptItems === null || acceptItems === '')) {
            newMissingFields.acceptItems = true;
        }

        if (ticketDetails.LensTypeChange && (acceptLens === null || acceptLens === '')) {
            newMissingFields.acceptLens = true;
        }

        for (let key in newMissingFields) {
            if (newMissingFields[key] === true) {
                isValid = false;
                break;
            }
        }

        setMissingFields(newMissingFields);
        return isValid;
    };



    const handleProcessAuthorise = async ({ }) => {
        let isComplete = true;
        const isValid = validateForm();

        if (!isValid) {
            return;
        }
   
        const request = {
            unqID: ticketDetails.UnqID,
            barcode: ticketDetails.Barcode,
            lensTypeChange: acceptLens,
            acceptUnauthorisedItems: acceptItems,
            orderNumber: ticketDetails.OrderNumber
        }

        setIsLoading(true);

        try {
            await authoriseOrder(request);
            setError(null);
        } catch (e) {
            isComplete = false;
            setError(e);
        } finally {
            setIsLoading(false);
            if (isComplete) { showHideModal(ticketDetails.UnqID); }
        }
    };

    return (
        <>
            <Modal open={isModalOpen}>
                <StyledModal>
                    <Card
                        label={`Authorise EyeTicket for ${eyeTicket.Employee}`}
                        headercolour={colour.sidebar}
                        headerTextColour={colour.font}
                        shadow={false}>
                        {isLoading &&
                            <ModalBody>
                                <LoadingScreen
                                    value="Please wait whilst we authorise the EyeTicket, this may take a few moments.."
                                    size="60px"
                                    borderColor="#d3d3d3"
                                    topColor={colour.sidebar}
                                />
                            </ModalBody>
                        }
                        {!isLoading && !error && (
                            <ModalBody>
                                <Heading>Please review the following EyeTicket</Heading>
                                <Alert type='empty'>The following EyeTicket requires authorisation due to the following reason: <b>{eyeTicket.ActionRequired}</b>. <br></br> Please review all requirements carefully before authorising this order.</Alert>

                                {eyeTicket.AcceptUnauthorisedItems &&
                                    <FieldContainer>
                                        <SelectWithLabel
                                            label="Accept/Reject Unauthorised Lens Coating"
                                            options={selectOptions}
                                            required={true}
                                            missing={missingFields.acceptItems}
                                            value={acceptItems}
                                            onChange={(e) => setAcceptItems(e.target.value)} />
                                    </FieldContainer>}
                                {eyeTicket.LensTypeChange &&
                                    <FieldContainer>
                                        <SelectWithLabel
                                            label="Accept/Reject Unauthorised Lens Type"
                                            options={selectOptions}
                                            required={true}
                                            missing={missingFields.acceptLens}
                                            value={acceptLens}
                                            onChange={(e) => setAcceptLens(e.target.value)} />
                                    </FieldContainer>}
                                <FieldContainer>
                                    <TextInputWithLabel
                                        label='Order No.'
                                        value={ticketDetails.OrderNumber}
                                        disabled={eyeTicket.OrderNumberRequired === 0}
                                        onChange={(event) => handleEnteredValues('OrderNumber', event.target.value)}
                                        missing={missingFields.OrderNumber}
                                        required={!eyeTicket.OrderNumberRequired === 0} />
                                </FieldContainer>

                                <FieldContainer>
                                    <TextInputWithLabel
                                        label='Company'
                                        value={eyeTicket.CompanyName}
                                        disabled />
                                </FieldContainer>
                                <FieldContainer>
                                    <TextInputWithLabel
                                        label="Price For Items In Description"
                                        type="number"
                                        value={eyeTicket?.PriceForItemsInDescription}
                                        currency="£"
                                        disabled />
                                </FieldContainer>
                                <FieldContainer>
                                    <TextInputWithLabel
                                        label="Price For AddOns In Description"
                                        type="number"
                                        value={eyeTicket?.PriceForAddOnsInDescription}
                                        currency="£"
                                        disabled />
                                </FieldContainer>
                                <FieldContainer>
                                    <RichTextBoxWithLabel
                                        label='Order Description'
                                        value={eyeTicket.Description}
                                        disabled />
                                </FieldContainer>
                                <FieldContainer>
                                    <TextInputWithLabel
                                        label='Optician'
                                        value={eyeTicket.Optician}
                                        disabled />
                                </FieldContainer>
                                <FieldContainer>
                                    <TextInputWithLabel
                                        label='Barcode'
                                        value={eyeTicket.Barcode}
                                        disabled />
                                </FieldContainer>
                                <FieldContainer>
                                    <TextInputWithLabel
                                        label='CostCentre'
                                        value={eyeTicket.CostCentre}
                                        disabled />
                                </FieldContainer>
                                <FieldContainer>
                                    <TextInputWithLabel
                                        label='Department'
                                        value={eyeTicket.Department}
                                        disabled />
                                </FieldContainer>
                            </ModalBody>
                        )}
                    </Card>
                    <ModalFooter>
                        {isLoading && <Alert type='information'>Loading..</Alert>}
                        {error && !isLoading && <Alert type='error'>Unable to process request due to <b>{error.message}</b></Alert>}
                        <CancelButton onClick={() => { showHideModal(); }}>Cancel</CancelButton>
                        <Button
                            onClick={handleProcessAuthorise}>Process</Button>
                    </ModalFooter>
                </StyledModal>
            </Modal>
        </>
    );
}
